import * as Collapsible from "@radix-ui/react-collapsible";
import React, { CSSProperties } from "react";
import { StyledComponentInnerOtherProps, StyledComponentPropsWithRef } from "styled-components";
import * as S from "./styled";

type BlockDOMProps = Omit<StyledComponentPropsWithRef<typeof S.Block>, "ref">;
type StyledBlockProps = BlockDOMProps &
    StyledComponentInnerOtherProps<typeof S.Block> & {
        [dataAttribute: `data-${string}`]: any;
    };

export type BlockProps = BlockDOMProps & {
    header?: React.ReactNode;
    headerWrap?: CSSProperties["flexWrap"];
    actions?: React.ReactNode;
    builtIn?: boolean;
    collapsible?: boolean;
    defaultOpen?: boolean;
    withoutContainerStyles?: boolean;
};

const Block: React.FC<BlockProps> = ({
    header,
    headerWrap = "wrap",
    actions,
    builtIn = false,
    collapsible = false,
    defaultOpen = false,
    withoutContainerStyles = false,
    children,
    ...props
}) => {
    const headerJsx = (header || actions || collapsible) && (
        <S.Header $collapsible={collapsible} $wrap={headerWrap}>
            <S.HeaderContent>{header}</S.HeaderContent>
            {(actions || collapsible) && (
                <S.HeaderContent>{collapsible ? <S.Chevron size={20} /> : actions}</S.HeaderContent>
            )}
        </S.Header>
    );

    const contentJsx = <S.BlockContent>{children}</S.BlockContent>;

    const blockProps: StyledBlockProps = {
        ...props,
        $withoutContainerStyles: withoutContainerStyles,
        "data-nesting": builtIn ? "built-in" : "normal",
        ...(headerJsx ? ({ "data-block-with-header": true } satisfies Partial<StyledBlockProps>) : {})
    };

    if (collapsible) {
        return (
            <Collapsible.Root asChild defaultOpen={defaultOpen}>
                <S.Block {...blockProps}>
                    <Collapsible.Trigger asChild>{headerJsx}</Collapsible.Trigger>
                    <S.AnimatedContent>{contentJsx}</S.AnimatedContent>
                </S.Block>
            </Collapsible.Root>
        );
    }

    return (
        <S.Block {...blockProps}>
            {headerJsx}
            {contentJsx}
        </S.Block>
    );
};

export default Block;
